import { actorCanReachTarget } from '../shared/proximity-browser';
import { cardEditQuery } from '../shared/card-edit-publication';
import { statusResumeProximity } from '../shared/status-resume-proximity';
import { refuseUnported } from '../janus-engine/unported.js';

// status-effect-actions.ts — status-effect MUTATION handlers (template.js cleanup roadmap T2.5, 2026-06-14).
//
// Byte-faithful port of the status-effect action layer carved out of template.js: the add-status
// quick-duration buttons, add / delete / delete-all-connections / modify / stackable-adjust /
// pause / resume handlers, the refresh-if-visible helpers, and longRestParty. Combat-relevant —
// ported verbatim (only `: any` params + `as HTMLInputElement` DOM casts added for strict TS).
//
// Public surface (window-attached — PHP onclick + render-bundle runtime callers):
//   setAddStatusDurationQuick, updateAddStatusQuickDurationButtons, addStatusEffect,
//   deleteCreatureStatusEffect, deleteCreatureStatusEffectAllConnections, modifyCreatureStatusEffect,
//   adjustStackableStatusEffect, pauseStatusEffect, resumeStatusEffect, longRestParty.
// Callers: options/tools/statuses/**.php (onclick), options/tables/table_item_status_effect_inc.php,
// the status-effect render bundle (render-status-form.ts / render-creature-status-form.ts via window),
// the turn-widget bundle (longRestParty onclick), and template.js loadPostFunctions()
// (updateAddStatusQuickDurationButtons at line ~3746 — OUTSIDE the carved range, so it MUST stay
// window-attached).
//
// Module-local (proven zero callers outside the carved range, repo-wide grep): the refresh-if-visible
// helpers + the after-mutation refresh gates.
//
// Loaded AFTER template.js; every dep resolves at call time (onclick / form-button / AJAX flow):
// BASE_URL (core-helpers), setTomSelectValue (tom-select-init.js), openSideLog (side-panels-init.js),
// getSidelogContext / queryData / validateContext (template.js).

function setAddStatusDurationQuick(durationInt: number | string, durationType: string): void {
    const intEl = document.getElementById('add_status_duration_int') as HTMLInputElement | null;
    if (intEl) {
        intEl.value = String(durationInt);
    }
    setTomSelectValue('add_status_duration_type', durationType);
    updateAddStatusQuickDurationButtons();
}

function updateAddStatusQuickDurationButtons(): void {
    const intEl = document.getElementById('add_status_duration_int') as HTMLInputElement | null;
    const typeEl = document.getElementById('add_status_duration_type') as HTMLInputElement | null;
    if (!intEl || !typeEl) {
        return;
    }

    const intVal = String(intEl.value).trim();
    const typeVal = typeEl.value;

    document.querySelectorAll('.add-status-quick-dur-btn').forEach((btn) => {
        const durType = btn.getAttribute('data-dur-type');
        const durInt = btn.getAttribute('data-dur-int');
        let active = false;
        if (durType === 'permanent') {
            active = typeVal === 'permanent';
        } else if (durInt != null && durInt !== '') {
            active = typeVal === durType && intVal === String(durInt);
        }
        btn.classList.toggle('active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
}

(function fateTideInitAddStatusQuickDurationSync() {
    if (window.__fateTideAddStatusQuickDurationBound) {
        return;
    }
    window.__fateTideAddStatusQuickDurationBound = true;
    document.addEventListener('change', function (e) {
        if (e.target && (e.target as HTMLElement).id === 'add_status_duration_type') {
            updateAddStatusQuickDurationButtons();
        }
    });
    document.addEventListener('input', function (e) {
        if (e.target && (e.target as HTMLElement).id === 'add_status_duration_int') {
            updateAddStatusQuickDurationButtons();
        }
    });
})();

async function addStatusEffect(status_target_id: string): Promise<void> {

    const sl_context = getSidelogContext();
    if (!sl_context) return;

    const {
        roll_private,
        encounter_id,
    } = sl_context;


    const status_caster_id = (document.getElementById('add_status_caster_id') as HTMLInputElement).value;
    // One "add" submit serves three mutually-exclusive pickers: a status_effect, an ability, or a
    // disease (all three apply through the same status path — the status commit resolves item_type
    // via ft_resolve_item_id_type). First filled picker wins, in form order.
    const firstFilledPickerValue = (...ids: string[]): string => {
        for (const id of ids) {
            const v = ((document.getElementById(id) as HTMLInputElement | null)?.value || '').trim();
            if (v) return v;
        }
        return '';
    };
    const status_item_id = firstFilledPickerValue('add_status', 'add_ability', 'add_disease');
    const duration_int = (document.getElementById('add_status_duration_int') as HTMLInputElement).value;
    const duration_type = (document.getElementById('add_status_duration_type') as HTMLInputElement).value;

    // Lane GMS S4 / A8 (2026-10-02, R-065): client truth ONLY — status_truth_commit_inc.php (the target row's authority, the actor revision,
    // the C1 claim served or the rebuild tail; a persistent-AoE ability's zone + initial occupants decided by the page, zone-add.ts).
    // options/tools/statuses/add_status_effect.php is DELETED: a page without the status seam is told to reload, never a legacy post.
    if (!window.ftTryCommitStatusAdd) {
        if (typeof displayWarning === 'function') displayWarning('The status was not added: this page is out of date. Reload the page and try again.');
        return;
    }
    await window.ftTryCommitStatusAdd({ targetId: status_target_id, statusItemId: status_item_id, casterId: status_caster_id, durationInt: duration_int,
        durationType: duration_type, rollPrivate: Number(roll_private) || 0, encounterId: encounter_id || undefined });
    openSideLog();
    // The applied caster delta reconciles the status list. Keep the pickers and drafts mounted.
}

async function deleteCreatureStatusEffect(status_id: string, campaign_id_override?: string, encounter_id_override?: string): Promise<void> {

    void campaign_id_override; void encounter_id_override; // kept for the callers' signature (the commit reads the holder's own context)
    // Lane GMS S5 / N6e (2026-10-02, R-065): client truth ONLY — delete_creature_status_effect_inc.php is DELETED; the seam locates the
    // holder (a creature or an item) and refuses with words what it cannot remove.
    if (typeof window.ftTryCommitStatusDelete !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusDelete({ statusId: status_id, scope: 'single' });
}

// Resolve the live campaign + encounter context from the FateTide cache (ft_boot session island,
// kept current by the WS deltas) rather than PHP-baked inline values or sidelog hidden inputs that
// can be empty depending on how a card was opened (e.g. openItemInfo on a status effect double-click,
// rendered without $campaign_arr in scope -> blank baked campaign_id -> log row stamped campaign_id
// NULL -> no campaign time in the removal card footer). campaign.id is the authoritative current
// campaign; the active encounter helper is the loaded-encounter state of truth.
// Returns '' for campaign_id on adventure (non-campaign) encounters — callers MUST tolerate that
// (no campaign means there is legitimately no campaign time to stamp).
function ftResolveCampaignEncounterContextFromCache(): { campaign_id: string; encounter_id: string } {
    const context = getSidelogContext();
    return {
        campaign_id: context?.campaign_id || '',
        encounter_id: context?.encounter_id || '',
    };
}

// GM "delete status effect (all connections)" from the status-effect item card
// (options/tables/table_item_status_effect_inc.php). Posts to the GM endpoint
// (delete_status_effect_all_connections_inc.php -> roll_type 'status_effect_all_connections_deleted').
// Sources campaign/encounter from the cache so the inserted log row carries campaign_id (and thus
// campaign time in the footer) whenever a campaign is loaded.
async function deleteStatusEffectAllConnections(status_id: string, caster_id?: string): Promise<void> {
    // Lane GMS S6 / N6e (2026-10-02, R-065): client truth ONLY (target-row authority, the status row's creator, the GM log row) —
    // delete_status_effect_all_connections_inc.php is DELETED.
    if (typeof window.ftTryCommitStatusDelete !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusDelete({ statusId: status_id, scope: 'all_gm', logCasterId: caster_id || undefined });
}

async function deleteCreatureStatusEffectAllConnections(status_id: string, campaign_id_override?: string, encounter_id_override?: string): Promise<void> {

    void campaign_id_override; void encounter_id_override; // kept for the callers' signature (the commit reads the holder's own context)
    // Lane GMS S6 / N6e (2026-10-02, R-065): client truth ONLY (the status pill) — delete_creature_status_effect_all_connections_inc.php is
    // DELETED; a connection group / a removal tail is a rebuild delete inside the commit, never a hand-back.
    if (typeof window.ftTryCommitStatusDelete !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusDelete({ statusId: status_id, scope: 'all' });
}

async function modifyCreatureStatusEffect(status_id: string): Promise<void> {

    const sl = getSidelogContext();
    if (!sl) return;

    const status_starts_campaign_year = (document.getElementById('status_starts_campaign_year') as HTMLInputElement).value;
    const status_starts_campaign_month = (document.getElementById('status_starts_campaign_month') as HTMLInputElement).value;
    const status_starts_campaign_day = (document.getElementById('status_starts_campaign_day') as HTMLInputElement).value;
    const status_starts_campaign_hour = (document.getElementById('status_starts_campaign_hour') as HTMLInputElement).value;
    const status_starts_campaign_minute = (document.getElementById('status_starts_campaign_minute') as HTMLInputElement).value;
    const status_starts_campaign_second = (document.getElementById('status_starts_campaign_second') as HTMLInputElement).value;

    const status_ends_campaign_year = (document.getElementById('status_ends_campaign_year') as HTMLInputElement).value;
    const status_ends_campaign_month = (document.getElementById('status_ends_campaign_month') as HTMLInputElement).value;
    const status_ends_campaign_day = (document.getElementById('status_ends_campaign_day') as HTMLInputElement).value;
    const status_ends_campaign_hour = (document.getElementById('status_ends_campaign_hour') as HTMLInputElement).value;
    const status_ends_campaign_minute = (document.getElementById('status_ends_campaign_minute') as HTMLInputElement).value;
    const status_ends_campaign_second = (document.getElementById('status_ends_campaign_second') as HTMLInputElement).value;

    // OPEN SIDELOG
    openSideLog();

    await cardEditQuery(
        BASE_URL + "options/tools/statuses/modify_status_effect_inc.php",
        'div_sidelog_warning',
        {
            roll_private: sl.roll_private,
            campaign_id: sl.campaign_id,
            status_id,
            status_starts_campaign_year,
            status_starts_campaign_month,
            status_starts_campaign_day,
            status_starts_campaign_hour,
            status_starts_campaign_minute,
            status_starts_campaign_second,
            status_ends_campaign_year,
            status_ends_campaign_month,
            status_ends_campaign_day,
            status_ends_campaign_hour,
            status_ends_campaign_minute,
            status_ends_campaign_second
        }
    );

}

async function refreshCreatureStatusEffectsListIfVisible(): Promise<void> {
    // Client-rendered "Modify Creature Status Effects" form (renderCreatureStatusForm): re-fetch the
    // caster blob fresh, then repaint its list from cache. Do NOT rely solely on the WS caster_update
    // echo — a stack adjust has no optimistic row change, so a missed/lagged echo would leave the N/M
    // count stale (the reported bug). fetchAndCacheCaster swallows its own errors.
    const clientList = document.querySelector("[data-ft-creature-status-list]") as HTMLElement | null;
    if (clientList) {
        const cId = clientList.getAttribute("data-ft-creature-status-list") || "";
        if (cId) {
            if (typeof window.fetchAndCacheCaster === "function") {
                try { await window.fetchAndCacheCaster(cId); } catch { /* repaint from whatever is cached */ }
            }
            window.ftRerenderCreatureStatusList?.(cId);
            return;
        }
    }

    // Legacy PHP creature form fallback (server re-query into its own container).
    const container = document.getElementById(
        "div_refresh_edit_current_creature_status_effects",
    ) as HTMLElement | null;
    if (!container || !container.dataset.encounterCreatureId) return;
    const sl_context = getSidelogContext();
    if (!sl_context) return;
    await queryData(
        BASE_URL +
            "options/tools/statuses/refresh_creature_status_effects_list_inc.php",
        "div_refresh_edit_current_creature_status_effects",
        {
            roll_private: sl_context.roll_private,
            campaign_id: sl_context.campaign_id,
            encounter_id: sl_context.encounter_id,
            caster_id: container.dataset.encounterCreatureId,
        },
    );
}

async function refreshInventoryStatusEffectsListIfVisible(): Promise<void> {
    const container = document.getElementById(
        "div_refresh_modify_inventory_status_effects",
    ) as HTMLElement | null;
    if (!container || !container.dataset.inventoryId) return;

    // Unified client mount (status-row.ts): repaint the shared rows from the freshly-cached inventory
    // node instead of re-querying the server list (which would briefly flash the degraded PHP fallback
    // look). Matches the creature form, which is WS/cache-driven via ftRerenderCreatureStatusList. The
    // mutation's own caster_update echo lands a final repaint a tick later regardless.
    if (
        container.hasAttribute("data-ft-inventory-status-list")
        && typeof window.ftRerenderInventoryStatusList === "function"
    ) {
        window.ftRerenderInventoryStatusList(container.dataset.inventoryId);
        return;
    }

    const sl_context = getSidelogContext();
    if (!sl_context) return;
    await queryData(
        BASE_URL +
            "options/tools/statuses/refresh_inventory_status_effects_list_inc.php",
        "div_refresh_modify_inventory_status_effects",
        {
            roll_private: sl_context.roll_private,
            campaign_id: sl_context.campaign_id,
            encounter_id: sl_context.encounter_id,
            inventory_id: container.dataset.inventoryId,
        },
    );
}

async function afterAdjustStackableStatusEffectRefreshIfOk(): Promise<void> {
    const warnEl = document.getElementById("div_sidelog_warning");
    if (
        warnEl
        && !warnEl.querySelector("h5.bg-warning")
    ) {
        await refreshCreatureStatusEffectsListIfVisible();
        await refreshInventoryStatusEffectsListIfVisible();
    }
}

async function adjustStackableStatusEffect(
    direction: string,
    status_id: string,
    campaign_id_override?: string,
    encounter_id_override?: string,
): Promise<void> {
    void campaign_id_override; void encounter_id_override; // kept for the callers' signature (the commit reads the row's own context)
    // Lane GMS S7 / N6e (2026-10-02, R-065): client truth ONLY (status_truth_commit_inc.php) — adjust_stackable_status_effect_inc.php is
    // DELETED; the seam locates the holder and refuses with words what it cannot change (an item's status, an unreadable holder).
    if (typeof window.ftTryCommitStatusStack !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusStack({ statusId: status_id, direction: direction === 'down' ? 'down' : 'up' });
}

async function afterPauseOrResumeStatusEffectRefreshCreatureListIfOk(): Promise<void> {
    const warnEl = document.getElementById("div_sidelog_warning");
    if (warnEl && !warnEl.querySelector("h5.bg-warning")) {
        await refreshCreatureStatusEffectsListIfVisible();
    }
}

// Pause / resume accept campaign + encounter OVERRIDES, exactly like deleteCreatureStatusEffect and
// adjustStackableStatusEffect above. WHY (2026-07-28): the renderers gate the button's VISIBILITY on
// a resolved campaign id, but these posted whatever getSidelogContext() happened to hold — so a
// surface with a server-stamped context but an empty/stale sidelog (e.g. a status card on item.php)
// could paint a Pause button whose mutation then died on "Campaign context is required". Taking the
// overrides lets the caller post exactly the context it gated on, so the button never lies. A caller
// that passes nothing (the PHP onclick surfaces) keeps the old sidelog-context behavior.
async function pauseStatusEffect(
    status_id: string,
    campaign_id_override?: string,
    encounter_id_override?: string,
): Promise<void> {

    void campaign_id_override; void encounter_id_override; // kept for the callers' signature (the commit reads the actor's own encounter)
    // Lane GMS S3 / N6e (2026-10-02, R-065): client truth ONLY (status_truth_commit_inc.php) — pause_status_effect_inc.php is DELETED; the
    // seam locates the holder and refuses with words what it cannot pause.
    if (typeof window.ftTryCommitStatusPause !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusPause({ statusId: status_id, action: 'pause' });
}

async function resumeStatusEffect(
    status_id: string,
    campaign_id_override?: string,
    encounter_id_override?: string,
): Promise<void> {

    void campaign_id_override; void encounter_id_override; // kept for the callers' signature (the commit reads the actor's own encounter)
    openSideLog();

    const host = window as unknown as {
        ftFindStatusInstance?: (id: string, ownerId?: string) => Record<string, unknown> | undefined;
        ftGetActiveCasterId?: () => string;
        fetchAndCacheCaster?: (id: string) => Promise<unknown>;
    };
    let status = host.ftFindStatusInstance?.(status_id);
    if (!status) {
        const mount = document.querySelector<HTMLElement>(`[data-ft-object-id="${CSS.escape(status_id)}"]`);
        const owner = mount?.dataset.ftTargetId || host.ftGetActiveCasterId?.();
        if (owner) await host.fetchAndCacheCaster?.(owner);
        status = host.ftFindStatusInstance?.(status_id, owner);
    }
    if (!status) { displayWarning('Could not load the activity. Refresh the encounter and try again.'); return; }
    const proximity = statusResumeProximity(status);
    if (proximity) {
        const within = await actorCanReachTarget(proximity.actor, proximity.target);
        if (within !== true) {
            displayWarning(within === false ? proximity.warning : 'Could not determine reach. Refresh the encounter and try again.');
            return;
        }
    }

    // Lane GMS S3 / N6e (2026-10-02, R-065): client truth ONLY, after the proximity gate above — resume_status_effect_inc.php is DELETED
    // (a rest's resume re-applies Sleeping through the shared browser status path); the seam refuses with words what it cannot resume.
    if (typeof window.ftTryCommitStatusPause !== 'function') { displayWarning('Reload the page to change this status.'); return; }
    await window.ftTryCommitStatusPause({ statusId: status_id, action: 'resume' });
}

// NOTE: joinCraftingStatus/leaveCraftingStatus were retired 2026-07-28 with the Phase-2
// primary/assist crafting model. Joining and leaving a craft are Work/Stop on the WIP object
// card (window.ftCraftWipAction, early-cards bundle).

async function longRestParty(): Promise<void> {

    const sl_context = getSidelogContext();
    if (!sl_context) return;

    const {
        campaign_id,
        encounter_id,
        roll_private,
    } = sl_context;

    const rules = {
        encounter_id: { type: 'uuid', value: encounter_id },
        campaign_id: { type: 'uuid', value: campaign_id },
    };
    const validationResult = validateContext(rules, 'longRestParty');
    if (!validationResult.valid) {
        console.error(validationResult.error);
        return;
    }

    openSideLog();

    const commit = (window as Window & { ftTryCommitPartyLongRest?: (input: { campaignId: string; encounterId: string; rollPrivate: number }) => Promise<unknown> }).ftTryCommitPartyLongRest;
    if (!commit) { refuseUnported('long-rest:browser-save-unavailable'); return; }
    try { await commit({ campaignId: String(campaign_id), encounterId: String(encounter_id), rollPrivate: Number(roll_private) || 0 }); }
    catch { displayWarning('The long-rest save could not be confirmed. Check the saved state before another rest.'); }

}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. All
// runtime-invoked (PHP onclick across options/tools/statuses/**, the status-effect render bundle,
// the turn-widget bundle's longRestParty onclick, and template.js loadPostFunctions →
// updateAddStatusQuickDurationButtons). The refresh-if-visible helpers + after-mutation gates stay
// module-local (repo-wide grep: zero callers outside the carved range).
// ===========================================================================================
declare global {
    interface Window {
        setAddStatusDurationQuick: typeof setAddStatusDurationQuick;
        updateAddStatusQuickDurationButtons: typeof updateAddStatusQuickDurationButtons;
        addStatusEffect: typeof addStatusEffect;
        deleteCreatureStatusEffect: typeof deleteCreatureStatusEffect;
        deleteCreatureStatusEffectAllConnections: typeof deleteCreatureStatusEffectAllConnections;
        deleteStatusEffectAllConnections: typeof deleteStatusEffectAllConnections;
        modifyCreatureStatusEffect: typeof modifyCreatureStatusEffect;
        adjustStackableStatusEffect: typeof adjustStackableStatusEffect;
        pauseStatusEffect: typeof pauseStatusEffect;
        resumeStatusEffect: typeof resumeStatusEffect;
        longRestParty: typeof longRestParty;
    }
}

Object.assign(window, {
    setAddStatusDurationQuick,
    updateAddStatusQuickDurationButtons,
    addStatusEffect,
    deleteCreatureStatusEffect,
    deleteCreatureStatusEffectAllConnections,
    deleteStatusEffectAllConnections,
    modifyCreatureStatusEffect,
    adjustStackableStatusEffect,
    pauseStatusEffect,
    resumeStatusEffect,
    longRestParty,
});

export {};
